import { AloftArea, AloftPrice } from '@aloft/toolkit-shared';
import { Text, View } from '@react-pdf/renderer';

import { getCostString } from '../../costApproach/utils/getCostString.ts';
import { getPricePerSquareFoot } from '../../costApproach/utils/getPricePerSquareFoot.ts';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles as fonts, gridStyles } from '../styles.ts';
import { costApproachTableData } from '../types.ts';

type CostApproachPageProps = {
  subjectAddress: string;
  costApproachData: costApproachTableData;
};

export const CostApproachPage = ({ subjectAddress, costApproachData }: CostApproachPageProps) => {
  const featureRow = (feature: { area?: AloftArea; cost?: AloftPrice }, name: string) => (
    <View key={name} style={gridStyles.gridRow}>
      <View style={gridStyles.gridCol}>
        <View style={{ ...gridStyles.gridCell, flex: 3 }}>
          <Text style={fonts.body}>{name}</Text>
        </View>
      </View>
      <View style={gridStyles.gridCol}>
        <View style={gridStyles.gridCell}>
          <Text style={fonts.body}>
            {feature.area ? feature.area.toSqFt().toString() : '0 sqft'}
          </Text>
        </View>
        <View style={gridStyles.gridCell}>
          <Text style={fonts.body}>
            {`${feature.area && feature.cost ? getPricePerSquareFoot(feature.area, feature.cost)?.toString() : '$0.00'} per sqft`}
          </Text>
        </View>
        <View style={{ ...gridStyles.gridCell, alignItems: 'flex-end' }}>
          <Text style={{ ...fonts.body, paddingRight: 8 }}>{`${getCostString(feature.cost)}`}</Text>
        </View>
      </View>
    </View>
  );

  return (
    <Tkv2PDFPage title="Cost Approach" address={subjectAddress}>
      <Text style={fonts.body}>
        The Replacement Cost New (RCN) of the subject was calculated using local data from the
        Craftsman National Building Cost Manual. The Opinion of Site Value was determined via the
        extraction method. This site value accounts for the raw land and the as-is value of the site
        improvements (i.e., grading, drains, landscaping, utilities, and more). Note that the
        dollars per square foot shown below are rounded to the nearest whole cent.
      </Text>
      <View style={gridStyles.gridContainer}>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.bodyLight}>Feature</Text>
          </View>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.bodyLight}>Cost Data</Text>
          </View>
        </View>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.bodyBold}>Opinion of Site Value</Text>
          </View>
          <View
            style={{
              ...gridStyles.gridCell,
              alignItems: 'flex-end',
              paddingLeft: 0,
              paddingRight: 8,
            }}
          >
            <Text style={fonts.bodyBold}>{getCostString(costApproachData.opinionOfSiteValue)}</Text>
          </View>
        </View>
        {[costApproachData.dwelling, costApproachData.basement].map((item, index) =>
          featureRow(item, index === 0 ? 'Dwelling' : 'Basement'),
        )}

        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.body}>Additional Structures</Text>
          </View>
          <View style={{ ...gridStyles.gridCell, alignItems: 'flex-end' }}>
            <Text style={{ ...fonts.body, paddingRight: 8 }}>
              {getCostString(costApproachData.additionalStructures)}
            </Text>
          </View>
        </View>
        {featureRow(costApproachData.garageCarport, 'Garage/Carport')}
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.bodyBold}>Total Estimate Cost New</Text>
          </View>
          <View
            style={{
              ...gridStyles.gridCell,
              alignItems: 'flex-end',
              paddingLeft: 0,
              paddingRight: 8,
            }}
          >
            <Text
              style={fonts.bodyBold}
            >{`${getCostString(costApproachData.totalEstimateCostNew)}`}</Text>
          </View>
        </View>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCol}>
            <View style={{ ...gridStyles.gridCell, flex: 4 }}>
              <Text style={fonts.body}>Less</Text>
            </View>
          </View>
          <View style={gridStyles.gridCol}>
            <View style={gridStyles.gridCell}>
              <Text style={fonts.body}>Physical</Text>
            </View>
            <View style={gridStyles.gridCell}>
              <Text style={fonts.body}>External</Text>
            </View>
            <View style={gridStyles.gridCell}>
              <Text style={fonts.body}>Functional</Text>
            </View>
            <View style={gridStyles.gridCell} />
          </View>
        </View>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCol}>
            <View style={{ ...gridStyles.gridCell, flex: 4 }}>
              <Text style={fonts.body}>Depreciation</Text>
            </View>
          </View>
          <View style={gridStyles.gridCol}>
            <View style={gridStyles.gridCell}>
              <Text style={fonts.body}>{costApproachData.depreciation.physical?.toString()}</Text>
            </View>
            <View style={gridStyles.gridCell}>
              <Text style={fonts.body}>{costApproachData.depreciation.external?.toString()}</Text>
            </View>

            <View style={gridStyles.gridCell}>
              <Text style={fonts.body}>{costApproachData.depreciation.functional?.toString()}</Text>
            </View>
            <View
              style={{
                ...gridStyles.gridCell,
                alignItems: 'flex-end',
                paddingLeft: 0,
                paddingRight: 8,
              }}
            >
              <Text
                style={fonts.body}
              >{`${getCostString(costApproachData.depreciation.total)}`}</Text>
            </View>
          </View>
        </View>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.bodyBold}>Depreciated Cost of Improvements</Text>
          </View>
          <View style={{ ...gridStyles.gridCell, alignItems: 'flex-end' }}>
            <Text
              style={{ ...fonts.bodyBold, paddingRight: 8, paddingLeft: 0 }}
            >{`${getCostString(costApproachData.depreciatedCostOfImprovement)}`}</Text>
          </View>
        </View>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.body}>&quot;As-Is&quot; Value of Site Improvements</Text>
          </View>
          <View
            style={{
              ...gridStyles.gridCell,
              alignItems: 'flex-end',
              paddingLeft: 0,
              paddingRight: 8,
            }}
          >
            <Text style={fonts.body}>Included in site value</Text>
          </View>
        </View>
        <View style={gridStyles.gridRow}>
          <View style={gridStyles.gridCell}>
            <Text style={fonts.bodyBold}>Indicated Value by Cost Approach</Text>
          </View>
          <View style={{ ...gridStyles.gridCell, alignItems: 'flex-end' }}>
            <Text
              style={{ ...fonts.bodyBold, paddingRight: 8 }}
            >{`${getCostString(costApproachData.indicatedValueByCostApproach)}`}</Text>
          </View>
        </View>
      </View>
      <View>
        <View style={gridStyles.row}>
          <View style={gridStyles.listItemDotSmall} />
          <Text style={fonts.body}>Source of cost data: Craftsman</Text>
        </View>
        <View style={gridStyles.row}>
          <View style={gridStyles.listItemDotSmall} />
          <Text
            style={fonts.body}
          >{`Effective date of cost data: ${costApproachData.effectiveDate ? `01/01/${costApproachData.effectiveDate}` : 'unknown'}`}</Text>
        </View>
        <View style={gridStyles.row}>
          <View style={gridStyles.listItemDotSmall} />
          <Text
            style={fonts.body}
          >{`Quality rating from cost service: ${costApproachData.qualityRating ? costApproachData.qualityRating.toFixed(2) : 'unknown'}`}</Text>
        </View>
        <View style={gridStyles.row}>
          <View style={gridStyles.listItemDotSmall} />
          <Text
            style={fonts.body}
          >{`Est. remaining economic life: ${costApproachData.remainingEconomicLife ? costApproachData.remainingEconomicLife : 0} years`}</Text>
        </View>
      </View>
    </Tkv2PDFPage>
  );
};
